/**
 * PATH: packages/tenant-shared/src/utils/tenantStorage.ts
 * FILE_ID: TENANTSHARED-UTILS-TENANTSTORAGE-001
 * PURPOSE: Tenant-scoped localStorage / sessionStorage / window-event
 *          name helpers. Single source of truth for the BUG-0012 +
 *          BUG-0029 fix that every tenant SPA needs (newmark, sam,
 *          and any future tenant on dashboard.quotech.ai).
 * WHY:     Both SAM and Newmark live on the same `dashboard.quotech.ai`
 *          origin, so they SHARE the browser's localStorage. Without
 *          per-tenant key scoping, opening Newmark mutates SAM's
 *          preferences and vice versa — a real reported bug class:
 *
 *            - "starred jobs are different between Newmark and SAM"
 *              (jobs-starred)
 *            - "my Newmark dashboard is full of 'New SAM version
 *              available' rows" (notifications, BUG-0012)
 *            - "my Newmark theme keeps reverting" (ass-theme key shared)
 *
 *          Pre-MR-N this lived at `apps/newmark/src/shared/utils/tenantStorage.ts`
 *          ONLY — the SAM bundle hard-coded `'sam.*'` literals everywhere
 *          (composeDockStore, draftStore, notificationStore, …) which
 *          re-introduced the bug from a different code path. Lifting the
 *          helper into @quotech/tenant-shared makes both tenants consume
 *          the same logic and lets the next round of MRs replace the
 *          remaining hard-coded literals with `tlsKey()` / `tenantEventName()`
 *          calls without forking again.
 *
 * FLOW:
 *   ┌──────────────┐   ┌─────────────────────────┐   ┌──────────────┐
 *   │ raw suffix   │ → │ resolveTenantNamespace  │ → │ "<ns>.<suf>" │
 *   │ ('foo.bar')  │   │  + slug validation      │   │  prefixed    │
 *   └──────────────┘   └─────────────────────────┘   └──────────────┘
 *
 * DEPENDENCIES:
 *   - import.meta.env.VITE_NOTIFICATION_NS — build-time env var that
 *     resolves the tenant slug. SAM omits it (defaults to `'sam'` for
 *     back-compat with years of `sam.*` user state). Newmark sets
 *     `VITE_NOTIFICATION_NS=newmark` in `.env.production`.
 *
 * RELATED:
 *   - packages/tenant-shared/src/stores/notificationStore.ts — first
 *     lifted consumer (uses `tenantEventName('notify')`).
 *   - apps/{newmark,sam}/src/shared/utils/tenantStorage.ts — thin
 *     re-export shims so existing imports still resolve.
 *   - apps/{newmark,sam}/src/features/email/lib/draftStore.ts — slated
 *     for the BUG-0029 follow-up MR that replaces the literal
 *     `'sam.email.compose.drafts.v3'` with `tlsKey('email.compose.drafts.v3')`.
 *   - apps/{newmark,sam}/src/features/email/compose/composeDockStore.ts
 *     — same.
 *
 * Migration note
 * ──────────────
 * Existing user state stored under `sam.*` is intentionally NOT
 * migrated to `newmark.*`. The data is already cross-contaminated
 * (both tenants were writing to the same key); a user's Newmark
 * preferences post-cutover start fresh, their SAM preferences remain
 * untouched. This matches the original notification-store fix's strategy.
 */

function _resolve(): string {
  const fromEnv = (import.meta.env.VITE_NOTIFICATION_NS as string | undefined) || ''
  const trimmed = fromEnv.trim()
  if (trimmed && /^[a-z0-9_-]+$/i.test(trimmed)) return trimmed.toLowerCase()
  // Back-compat for SAM, whose .env doesn't set VITE_NOTIFICATION_NS
  // (the bundle pre-dates this convention). Newmark's .env.production
  // sets `VITE_NOTIFICATION_NS=newmark`, so this fallback never fires
  // there.
  return 'sam'
}

/** Resolved once per page load. The env var is build-time so the
 *  result never changes within a session. */
export const TENANT_NAMESPACE: string = _resolve()

/** Build a tenant-scoped localStorage / sessionStorage key. */
export function tlsKey(suffix: string): string {
  if (typeof suffix !== 'string' || suffix.length === 0) {
    throw new Error('tlsKey: suffix must be a non-empty string')
  }
  return `${TENANT_NAMESPACE}.${suffix}`
}

/** Build a tenant-scoped CustomEvent name for window-bus dispatch. */
export function tenantEventName(suffix: string): string {
  if (typeof suffix !== 'string' || suffix.length === 0) {
    throw new Error('tenantEventName: suffix must be a non-empty string')
  }
  return `${TENANT_NAMESPACE}.${suffix}`
}
